import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import {
  Plus, Search, RefreshCw, DollarSign, Send, CheckCircle, Clock, FileText, X, Trash2
} from 'lucide-react';

const STATUS_COLORS = {
  DRAFT: 'bg-slate-500', SENT: 'bg-blue-500', PAID: 'bg-green-500', OVERDUE: 'bg-red-500', CANCELLED: 'bg-slate-600'
};

const Invoices = () => {
  const { user } = useAuth();
  const token = localStorage.getItem('nexus_token');
  const [invoices, setInvoices] = useState([]);
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [showCreate, setShowCreate] = useState(false);

  const generateLineItemId = () => `line-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  const [form, setForm] = useState({
    case_id: '', client_id: '', billing_type: 'HOURLY',
    line_items: [{ id: generateLineItemId(), description: '', quantity: 1, rate: 0, amount: 0 }],
    tax_rate: 0, due_date: '', notes: ''
  });

  const isAdmin = user?.role === 'department_admin' || user?.role === 'main_hub';
  const [showPayment, setShowPayment] = useState(false);
  const [payingInvoice, setPayingInvoice] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ payment_method: 'bank_transfer', payment_reference: '', payment_notes: '' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [invRes, casesRes, clientsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/invoices${statusFilter !== 'ALL' ? `?status=${statusFilter}` : ''}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${BACKEND_URL}/api/cases`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${BACKEND_URL}/api/clients`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (invRes.ok) setInvoices(await invRes.json());
      if (casesRes.ok) setCases(await casesRes.json());
      if (clientsRes.ok) setClients(await clientsRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addLineItem = () => {
    setForm(prev => ({ ...prev, line_items: [...prev.line_items, { id: generateLineItemId(), description: '', quantity: 1, rate: 0, amount: 0 }] }));
  };

  const updateLineItem = (idx, field, value) => {
    setForm(prev => {
      const items = [...prev.line_items];
      items[idx] = { ...items[idx], [field]: value };
      if (field === 'quantity' || field === 'rate') {
        items[idx].amount = Number(items[idx].quantity) * Number(items[idx].rate);
      }
      return { ...prev, line_items: items };
    });
  };

  const removeLineItem = (idx) => {
    setForm(prev => ({ ...prev, line_items: prev.line_items.filter((_, i) => i !== idx) }));
  };

  const calcSubtotal = () => form.line_items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  const calcTax = () => calcSubtotal() * (Number(form.tax_rate) || 0) / 100;
  const calcTotal = () => calcSubtotal() + calcTax();

  const handleCreate = async () => {
    if (!form.case_id || !form.client_id) {
      toast({ title: 'Error', description: 'Case and Client are required', variant: 'destructive' });
      return;
    }
    try {
      const body = {
        case_id: form.case_id,
        client_id: form.client_id,
        billing_type: form.billing_type,
        line_items: form.line_items,
        subtotal: calcSubtotal(),
        tax_rate: Number(form.tax_rate) || 0,
        tax_amount: calcTax(),
        total_amount: calcTotal(),
        due_date: form.due_date || null,
        notes: form.notes || null,
      };
      const res = await fetch(`${BACKEND_URL}/api/invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        toast({ title: 'Success', description: 'Invoice created' });
        setShowCreate(false);
        setForm({ case_id: '', client_id: '', billing_type: 'HOURLY', line_items: [{ description: '', quantity: 1, rate: 0, amount: 0 }], tax_rate: 0, due_date: '', notes: '' });
        fetchData();
      } else {
        const err = await res.json();
        throw new Error(err.detail || 'Failed');
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleSend = async (id) => {
    await fetch(`${BACKEND_URL}/api/invoices/${id}/send`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    toast({ title: 'Success', description: 'Invoice sent' });
    fetchData();
  };

  const handleMarkPaid = async (id) => {
    await fetch(`${BACKEND_URL}/api/invoices/${id}/mark-paid`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    toast({ title: 'Success', description: 'Invoice marked as paid' });
    fetchData();
  };

  const handleRecordPayment = async () => {
    if (!payingInvoice) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/invoices/${payingInvoice.id}/pay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(paymentForm)
      });
      if (res.ok) {
        toast({ title: 'Success', description: 'Payment recorded' });
        setShowPayment(false);
        setPayingInvoice(null);
        setPaymentForm({ payment_method: 'bank_transfer', payment_reference: '', payment_notes: '' });
        fetchData();
      } else {
        const err = await res.json();
        toast({ title: 'Error', description: err.detail || 'Failed', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const filtered = invoices.filter(inv => {
    if (!search) return true;
    const s = search.toLowerCase();
    return inv.invoice_number?.toLowerCase().includes(s) || inv.case_id?.toLowerCase().includes(s);
  });

  const getClientName = (clientId) => clients.find(c => c.id === clientId)?.name || clientId;
  const getCaseTitle = (caseId) => { const c = cases.find(x => x.id === caseId); return c ? `${c.case_number}` : caseId; };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Invoices</h1>
          <p className="text-slate-400">Billing and payment tracking</p>
        </div>
        {isAdmin && (
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="bg-[#0F172A] hover:bg-[#1E293B] text-white" data-testid="create-invoice-btn">
                <Plus className="w-4 h-4 mr-2" /> New Invoice
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Create Invoice</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Case *</Label>
                    <Select value={form.case_id} onValueChange={v => setForm({ ...form, case_id: v })}>
                      <SelectTrigger className="bg-slate-50 border-slate-300 text-slate-900" data-testid="invoice-case-select"><SelectValue placeholder="Select case" /></SelectTrigger>
                      <SelectContent>
                        {cases.map(c => <SelectItem key={c.id} value={c.id}>{c.case_number} - {c.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Client *</Label>
                    <Select value={form.client_id} onValueChange={v => setForm({ ...form, client_id: v })}>
                      <SelectTrigger className="bg-slate-50 border-slate-300 text-slate-900" data-testid="invoice-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                      <SelectContent>
                        {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Billing Type</Label>
                    <Select value={form.billing_type} onValueChange={v => setForm({ ...form, billing_type: v })}>
                      <SelectTrigger className="bg-slate-50 border-slate-300 text-slate-900"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="HOURLY">Hourly</SelectItem>
                        <SelectItem value="FLAT_RATE">Flat Rate</SelectItem>
                        <SelectItem value="CASE_BASED">Case Based</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Due Date</Label>
                    <Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })}
                      className="bg-slate-50 border-slate-300 text-slate-900" />
                  </div>
                </div>

                {/* Line Items */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Line Items</Label>
                    <Button size="sm" variant="outline" className="border-slate-300 text-slate-300" onClick={addLineItem}>
                      <Plus className="w-3 h-3 mr-1" /> Add Item
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {form.line_items.map((item, idx) => (
                      <div key={item.id} className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-5">
                          {idx === 0 && <Label className="text-xs">Description</Label>}
                          <Input value={item.description} onChange={e => updateLineItem(idx, 'description', e.target.value)}
                            className="bg-slate-50 border-slate-300 text-slate-900 text-sm" placeholder="Service description" />
                        </div>
                        <div className="col-span-2">
                          {idx === 0 && <Label className="text-xs">Qty</Label>}
                          <Input type="number" value={item.quantity} onChange={e => updateLineItem(idx, 'quantity', e.target.value)}
                            className="bg-slate-50 border-slate-300 text-slate-900 text-sm" />
                        </div>
                        <div className="col-span-2">
                          {idx === 0 && <Label className="text-xs">Rate ($)</Label>}
                          <Input type="number" value={item.rate} onChange={e => updateLineItem(idx, 'rate', e.target.value)}
                            className="bg-slate-50 border-slate-300 text-slate-900 text-sm" />
                        </div>
                        <div className="col-span-2">
                          {idx === 0 && <Label className="text-xs">Amount</Label>}
                          <div className="text-slate-900 text-sm p-2 bg-slate-50 border border-slate-300 rounded-md">${(Number(item.amount) || 0).toFixed(2)}</div>
                        </div>
                        <div className="col-span-1">
                          {form.line_items.length > 1 && (
                            <button onClick={() => removeLineItem(idx)} className="text-slate-500 hover:text-red-400 p-2">
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Totals */}
                <div className="border-t border-slate-700 pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Subtotal</span>
                    <span className="text-white">${calcSubtotal().toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-400">Tax Rate (%)</span>
                      <Input type="number" value={form.tax_rate} onChange={e => setForm({ ...form, tax_rate: e.target.value })}
                        className="bg-slate-50 border-slate-300 text-slate-900 w-20 text-sm" />
                    </div>
                    <span className="text-white">${calcTax().toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold">
                    <span className="text-white">Total</span>
                    <span className="text-amber-400">${calcTotal().toFixed(2)}</span>
                  </div>
                </div>

                <div>
                  <Label>Notes</Label>
                  <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                    className="bg-slate-50 border-slate-300 text-slate-900" rows={2} />
                </div>

                <Button onClick={handleCreate} className="w-full bg-[#0F172A] hover:bg-[#1E293B] text-white" data-testid="submit-invoice-btn">
                  Create Invoice
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input value={search} onChange={e => setSearch(e.target.value)}
                className="bg-slate-50 border-slate-300 text-slate-900 pl-10" placeholder="Search invoices..." />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="bg-slate-50 border-slate-300 text-slate-900"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Statuses</SelectItem>
                <SelectItem value="DRAFT">Draft</SelectItem>
                <SelectItem value="SENT">Sent</SelectItem>
                <SelectItem value="PAID">Paid</SelectItem>
                <SelectItem value="OVERDUE">Overdue</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" className="border-slate-300 text-slate-300" onClick={fetchData}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Invoice List */}
      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading invoices...</div>
      ) : filtered.length === 0 ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="p-12 text-center">
            <DollarSign className="w-16 h-16 mx-auto text-slate-600 mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Invoices</h3>
            <p className="text-slate-400">{search || statusFilter !== 'ALL' ? 'Try different filters' : 'Create your first invoice'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(inv => (
            <Card key={inv.id} className="bg-white border-slate-200" data-testid={`invoice-card-${inv.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-amber-400 font-mono text-sm">{inv.invoice_number}</span>
                      <Badge className={`${STATUS_COLORS[inv.status] || 'bg-slate-500'} text-white text-xs`}>{inv.status}</Badge>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400">
                      <span><FileText className="w-3 h-3 inline mr-1" />{getCaseTitle(inv.case_id)}</span>
                      <span>Client: {getClientName(inv.client_id)}</span>
                      {inv.due_date && <span><Clock className="w-3 h-3 inline mr-1" />Due: {inv.due_date}</span>}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {inv.line_items?.length || 0} items &middot; {inv.billing_type?.replace(/_/g, ' ')}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-xl font-bold text-slate-900">${(inv.total_amount || 0).toFixed(2)}</p>
                      {inv.tax_amount > 0 && <p className="text-xs text-slate-500">Tax: ${inv.tax_amount.toFixed(2)}</p>}
                    </div>
                    {isAdmin && (
                      <div className="flex gap-1">
                        {inv.status === 'DRAFT' && (
                          <Button size="sm" variant="outline" className="border-blue-500 text-blue-400" onClick={() => handleSend(inv.id)} data-testid={`send-invoice-${inv.id}`}>
                            <Send className="w-3 h-3 mr-1" /> Send
                          </Button>
                        )}
                        {inv.status === 'SENT' && (
                          <>
                            <Button size="sm" variant="outline" className="border-green-500 text-green-500" onClick={() => handleMarkPaid(inv.id)} data-testid={`mark-paid-${inv.id}`}>
                              <CheckCircle className="w-3 h-3 mr-1" /> Quick Paid
                            </Button>
                            <Button size="sm" variant="outline" className="border-slate-300 text-slate-600" onClick={() => { setPayingInvoice(inv); setShowPayment(true); }} data-testid={`record-payment-${inv.id}`}>
                              <DollarSign className="w-3 h-3 mr-1" /> Record
                            </Button>
                          </>
                        )}
                        {inv.paid_at && (
                          <span className="text-xs text-green-600 font-mono">Paid {new Date(inv.paid_at).toLocaleDateString()}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Record Payment Dialog */}
      <Dialog open={showPayment} onOpenChange={setShowPayment}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md">
          <DialogHeader><DialogTitle>Record Payment for {payingInvoice?.invoice_number}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="text-center p-3 bg-slate-50 rounded-sm border border-slate-200">
              <p className="text-sm text-slate-500">Amount Due</p>
              <p className="text-2xl font-bold text-slate-900">${(payingInvoice?.total_amount || 0).toFixed(2)}</p>
            </div>
            <div>
              <Label>Payment Method</Label>
              <Select value={paymentForm.payment_method} onValueChange={v => setPaymentForm({ ...paymentForm, payment_method: v })}>
                <SelectTrigger className="border-slate-300" data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="credit_card">Credit Card</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="wire">Wire Transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reference Number</Label>
              <Input value={paymentForm.payment_reference} onChange={e => setPaymentForm({ ...paymentForm, payment_reference: e.target.value })}
                className="border-slate-300" placeholder="Check #, Transaction ID, etc." data-testid="payment-reference-input" />
            </div>
            <div>
              <Label>Payment Notes</Label>
              <Textarea value={paymentForm.payment_notes} onChange={e => setPaymentForm({ ...paymentForm, payment_notes: e.target.value })}
                className="border-slate-300" rows={2} placeholder="Optional notes..." />
            </div>
            <Button onClick={handleRecordPayment} className="w-full bg-green-600 hover:bg-green-700 text-white" data-testid="confirm-payment-btn">
              Confirm Payment
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Invoices;
